import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { clearModuleCache } from '@/lib/hasModuleAccess';

// ── Types ──────────────────────────────────────────────────────────────────────
interface NavItemDef {
  path: string;
  label: string;
  icon: string;
  badgeKey?: 'modules' | 'domains';
  /** When set, active only when ?tab matches this value at pathname */
  tabMatch?: string;
  /** When true, this item is active only when at pathname with NO specific tab */
  noTab?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItemDef[];
}

// ── Navigation structure ───────────────────────────────────────────────────────
const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Platform',
    items: [
      { path: '/sa/dashboard',  label: 'Dashboard',     icon: 'ri-dashboard-3-line', noTab: true },
      { path: '/sa/status',     label: 'Status Center', icon: 'ri-pulse-line',       noTab: true },
      { path: '/sa/tenants',    label: 'Tenants',       icon: 'ri-building-2-line' },
      { path: '/sa/users',      label: 'Users',         icon: 'ri-team-line' },
    ],
  },
  {
    label: 'Monetization',
    items: [
      { path: '/sa/plans',           label: 'Plans',           icon: 'ri-price-tag-3-line' },
      { path: '/sa/modules',         label: 'Modules',         icon: 'ri-puzzle-line' },
      { path: '/sa/billing',         label: 'Billing',         icon: 'ri-coins-line' },
      { path: '/sa/module-requests', label: 'Module Requests', icon: 'ri-add-box-line',   badgeKey: 'modules' },
      { path: '/sa/domain-requests', label: 'Domain Requests', icon: 'ri-global-line',    badgeKey: 'domains' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { path: '/sa/audit-logs',          label: 'Audit Logs',       icon: 'ri-file-list-3-line' },
      { path: '/sa/security-audit',    label: 'Security Audit',    icon: 'ri-shield-check-line' },
      { path: '/sa/deployment',     label: 'Deployment',        icon: 'ri-rocket-line' },
      { path: '/sa/storage',              label: 'Storage',    icon: 'ri-hard-drive-2-line' },
      { path: '/sa/settings',            label: 'Settings',   icon: 'ri-settings-4-line' },
    ],
  },
];

// ── Single nav item ────────────────────────────────────────────────────────────
interface NavItemProps {
  item: NavItemDef;
  pendingModules: number;
  pendingDomains: number;
}

function NavItem({ item, pendingModules, pendingDomains }: NavItemProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab');

  // Determine active state
  const isActive = (() => {
    // Strip query from path to get base pathname
    const basePath = item.path.split('?')[0];
    if (location.pathname !== basePath) return false;

    if (item.tabMatch) {
      // e.g. Storage → active only when ?tab=storage
      return currentTab === item.tabMatch;
    }
    if (item.noTab) {
      // e.g. Status Center → active only when NO tab (or any tab except storage-specific one)
      // Dashboard: /sa/dashboard never has tab, so always true when pathname matches
      if (basePath === '/sa/status') return !currentTab;
      return true;
    }
    // Default: match by pathname only
    return true;
  })();

  const badge = item.badgeKey === 'modules' ? pendingModules
    : item.badgeKey === 'domains' ? pendingDomains : 0;

  return (
    <Link
      to={item.path}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer ${
        isActive
          ? 'bg-blue-50 text-blue-700 font-medium border-l-2 border-blue-500 pl-[10px]'
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
      }`}
    >
      <span className="w-4 h-4 flex items-center justify-center shrink-0">
        <i className={item.icon}></i>
      </span>
      <span className="flex-1 leading-none">{item.label}</span>
      {badge > 0 && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-white text-[10px] font-bold shrink-0">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
export default function SuperAdminSidebar() {
  const navigate = useNavigate();
  const [pendingModules, setPendingModules] = useState(0);
  const [pendingDomains, setPendingDomains] = useState(0);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const check = () => {
      setIsImpersonating(!!localStorage.getItem('impersonated_tenant_id'));
    };
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);

  useEffect(() => {
    Promise.all([
      supabase
        .from('tenant_module_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending'),
      supabase
        .from('tenant_domain_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending'),
    ]).then(([modRes, domRes]) => {
      if (modRes.count != null) setPendingModules(modRes.count);
      if (domRes.count != null) setPendingDomains(domRes.count);
    });
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    clearModuleCache();
    navigate('/login');
  };

  const totalPending = pendingModules + pendingDomains;

  return (
    <aside className="w-60 min-h-screen bg-white border-r border-slate-200 flex flex-col shrink-0">

      {/* ── Logo ── */}
      <div className="px-5 py-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <img
            src="https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/6cb7fb8d08f57254bfb69080e4b6eb8a.png"
            alt="Pixory AI"
            className="h-7 w-auto object-contain"
          />
          <div>
            <p className="text-slate-900 text-xs font-semibold tracking-widest">Pixory AI</p>
            <p className="text-blue-600 text-[10px] font-bold tracking-widest uppercase">Super Admin</p>
          </div>
        </div>
      </div>

      {/* ── SA Badge ── */}
      <div className="mx-4 mt-4 mb-1 px-3 py-2.5 rounded-xl bg-blue-50 border border-blue-100">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-100 text-blue-600 shrink-0">
            <i className="ri-shield-star-fill text-xs"></i>
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-blue-700 text-xs font-semibold leading-none mb-0.5">Control Total</p>
            <p className="text-blue-400/70 text-[10px] leading-none">Plataforma global</p>
          </div>
          {totalPending > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-white text-[10px] font-bold shrink-0">
              {totalPending > 99 ? '99+' : totalPending}
            </span>
          )}
        </div>
      </div>

      {/* ── Nav groups ── */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto">
        {NAV_GROUPS.map((group, gi) => (
          <div key={group.label} className={gi > 0 ? 'mt-5' : ''}>
            {/* Group header */}
            <p className="px-3 mb-1.5 text-[10px] font-bold tracking-[0.12em] uppercase text-slate-400 select-none">
              {group.label}
            </p>
            {/* Items */}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  pendingModules={pendingModules}
                  pendingDomains={pendingDomains}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* ── Divider ── */}
      <div className="mx-4 border-t border-slate-100" />

      {/* ── Footer actions ── */}
      <div className="px-4 py-4 flex flex-col gap-1.5">
        {isImpersonating && (
          <Link
            to="/admin"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 text-xs transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className="ri-arrow-go-back-line text-xs"></i>
            </span>
            Ir al Admin Tenant
          </Link>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-red-400 hover:text-red-500 hover:bg-red-50 text-xs transition-colors cursor-pointer whitespace-nowrap w-full"
        >
          <span className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-logout-box-r-line text-xs"></i>
          </span>
          Cerrar sesión
        </button>
      </div>
    </aside>
  );
}
